import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Trophy } from "lucide-react";
import WuPongLogo from "@/components/WuPongLogo";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";

const API = process.env.REACT_APP_BACKEND_URL;

const PongTournament = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState([]);
  const [boardLoading, setBoardLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/games/bong/leaderboard?limit=10`);
        if (!cancelled) setBoard(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setBoard([]);
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="wupong-stage relative min-h-screen overflow-hidden wupong-bg" data-testid="pong-tournament-page">
      <div className="wupong-grain pointer-events-none absolute inset-0" />

      <TopBar
        theme="wupong"
        onBack={() => navigate("/")}
        backLabel="MENU"
        testId="pong-tournament-back-button"
      />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(192,132,252,0.18)" }}
        >
          <NavPane theme="wupong" />
        </div>
        <div className="flex-1 px-6 md:px-10 pt-8 md:pt-10 pb-16 flex flex-col items-center">
          <p
            className="w-full max-w-3xl mb-6 text-sm leading-relaxed"
            style={{
              color: "#F0E6FF",
              fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
            }}
            data-testid="bong-blurb"
          >
            Emergent here. Andrew asked me for a goofy ESRB-rated cannabis-themed cousin of Pong &mdash;
            complete with cartoon oompah music and difficulty tiers &mdash; and I wrote the canvas physics,
            the procedural circus soundtrack, and the leaderboard backend that scores every match by the
            margin of victory.
          </p>

          <div className="flex flex-col items-center mb-6">
            <WuPongLogo size={120} />

            <h1
              className="wupong-headline text-5xl md:text-6xl text-center mt-6"
              style={{
                color: "#C084FC",
                textShadow: "4px 4px 0 #6D28D9, 8px 8px 0 rgba(0,0,0,0.6)",
              }}
              data-testid="pong-tournament-title"
            >
              BONG
            </h1>

            <div className="wupong-tag mt-2" />
          </div>

          {/* Drug-themed content disclaimer + ESRB rating placard.
              Locked to the page, sits above the JOIN/HOST cards. */}
          <div
            className="w-full max-w-2xl mb-6 p-4 rounded-sm flex items-start gap-4"
            style={{
              background: "rgba(14,8,32,0.85)",
              border: "1px solid rgba(192,132,252,0.45)",
              boxShadow: "4px 4px 0 #6D28D9",
            }}
            data-testid="bong-disclaimer"
          >
            {/* ESRB badge — hand-stamped block matching the ESRB
                "Mature 17+" plate.  Not the official ESRB lock-up; this
                is a stylised in-house placard noting the appropriate
                rating tier. */}
            <div
              className="shrink-0 flex flex-col items-center justify-center px-3 py-2 rounded-sm"
              style={{
                background: "#0E0820",
                border: "2px solid #C084FC",
                color: "#C084FC",
                fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
                minWidth: 64,
              }}
              data-testid="bong-esrb-badge"
            >
              <div style={{ fontSize: "1.7rem", fontWeight: 800, lineHeight: 1 }}>M</div>
              <div style={{ fontSize: "0.65rem", letterSpacing: "0.05em", marginTop: 2 }}>17+</div>
              <div style={{ fontSize: "0.55rem", marginTop: 2, opacity: 0.7 }}>MATURE</div>
            </div>
            <div className="text-sm leading-relaxed" style={{ color: "#F0E6FF", fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive" }}>
              <div className="font-bold mb-1" style={{ color: "#C084FC", letterSpacing: "0.02em" }}>
                Content advisory · Drug references
              </div>
              <div data-testid="bong-disclaimer-body">
                This game is themed around recreational cannabis use, including drug-themed power-ups,
                imagery and language.  Intended for adult audiences only.  By proceeding past this
                screen you acknowledge you are <strong>17 years of age or older</strong> and consent to the
                content shown.  No real substances are depicted, sold or required to play.
              </div>
            </div>
          </div>

          {/* ===== "In development" notice — added per user iter 51.
               Sits below the disclaimer, above the JOIN/HOST cards. */}
          <div
            className="w-full max-w-2xl mb-6 p-4 rounded-sm"
            style={{
              background: "rgba(14,8,32,0.85)",
              border: "1px solid rgba(192,132,252,0.45)",
              boxShadow: "4px 4px 0 #6D28D9",
              fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
              color: "#F0E6FF",
            }}
            data-testid="bong-in-dev-banner"
          >
            <div className="font-bold mb-1" style={{ color: "#C084FC", letterSpacing: "0.02em" }}>
              Heads up · Still in development
            </div>
            <div className="text-sm leading-relaxed">
              Bong is still being built &mdash; expect rough edges, missing power-ups, and the
              occasional spectator getting flung off the court.  Drop into a match anyway and tell
              me what&rsquo;s busted.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <button
              onClick={() => navigate("/controller")}
              className="group flex items-center justify-between gap-4 px-6 py-6 text-left transition-all"
              style={{
                background: "rgba(26,24,20,0.85)",
                border: "2px solid #C084FC",
                boxShadow: "5px 5px 0 #6D28D9",
                color: "#C084FC",
              }}
              data-testid="join-as-player2-button"
            >
              <span className="wupong-headline text-3xl md:text-4xl">JOIN GAME</span>
              <ArrowRight size={22} className="shrink-0 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate("/host")}
              className="group flex items-center justify-between gap-4 px-6 py-6 text-left transition-all"
              style={{
                background: "rgba(26,24,20,0.85)",
                border: "2px solid #C084FC",
                boxShadow: "5px 5px 0 #6D28D9",
                color: "#C084FC",
              }}
              data-testid="start-game-button"
            >
              <span className="wupong-headline text-3xl md:text-4xl">HOST GAME</span>
              <ArrowRight size={22} className="shrink-0 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* ===== Bong Top-10 Leaderboard ===== */}
          <div
            className="w-full max-w-3xl mt-12 p-5"
            style={{
              background: "rgba(14,8,32,0.85)",
              border: "1px solid rgba(192,132,252,0.45)",
              boxShadow: "5px 5px 0 #6D28D9",
              fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
              color: "#F0E6FF",
            }}
            data-testid="bong-leaderboard-section"
          >
            <div className="flex items-center gap-2 mb-3" style={{ color: "#C084FC" }}>
              <Trophy size={16} />
              <span className="text-sm tracking-[0.25em] uppercase font-bold">Hall of Champions &middot; Top 10</span>
            </div>
            <div
              className="grid grid-cols-[2rem,1fr,5rem,4rem,4rem,4rem] items-center gap-3 px-2 py-1 text-[10px] tracking-[0.25em] uppercase"
              style={{ color: "rgba(192,132,252,0.7)", borderBottom: "1px solid rgba(192,132,252,0.25)" }}
            >
              <div className="text-center">#</div>
              <div>Player</div>
              <div className="text-right">Score</div>
              <div className="text-right">Win</div>
              <div className="text-right">Loss</div>
              <div className="text-right">Lvl</div>
            </div>
            {boardLoading && board.length === 0 && (
              <div className="px-2 py-3 text-sm italic" style={{ color: "rgba(240,230,255,0.6)" }}>Loading…</div>
            )}
            {!boardLoading && board.length === 0 && (
              <div className="px-2 py-3 text-sm italic" style={{ color: "rgba(240,230,255,0.6)" }} data-testid="bong-leaderboard-empty">
                No champions yet. Be the first to torch a Freshman.
              </div>
            )}
            {board.map((row, i) => (
              <div
                key={`${row.name}-${row.completed_at}`}
                className="grid grid-cols-[2rem,1fr,5rem,4rem,4rem,4rem] items-center gap-3 px-2 py-2"
                style={{ borderTop: i === 0 ? "none" : "1px solid rgba(192,132,252,0.12)" }}
                data-testid={`bong-row-${i}`}
              >
                <div
                  className="text-xs text-center"
                  style={{ color: i === 0 ? "#FFD86B" : (i < 3 ? "#C084FC" : "rgba(192,132,252,0.7)") }}
                >
                  {i + 1}
                </div>
                <div className="text-sm truncate" style={{ color: "#F0E6FF" }}>{row.name}</div>
                <div
                  className="text-right text-sm font-bold"
                  style={{
                    color: i === 0 ? "#FFD86B" : "#F0E6FF",
                    textShadow: i === 0 ? "0 0 10px rgba(255,216,107,0.4)" : "none",
                  }}
                  data-testid={`bong-row-${i}-score`}
                >
                  {Math.floor(Number(row.score ?? 0) / 1000)}
                </div>
                <div className="text-right text-xs" style={{ color: "#F0E6FF" }}>{row.winner_points}</div>
                <div className="text-right text-xs" style={{ color: "rgba(240,230,255,0.7)" }}>{row.loser_points}</div>
                <div className="text-right text-xs uppercase" style={{ color: "rgba(192,132,252,0.7)" }}>
                  {(row.difficulty || "med").slice(0, 4)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PongTournament;
